import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import CommonLayout from "./CommonLayout";
import {
  useGetHolidayPackageQuery,
  useUpdateHolidayPackageMutation,
  useDuplicateHolidayPackageMutation
} from "application/features/holidayPackage/holidayPackageApiSlice";
import {
  selectEditingHolidayPackage,
  setEditingHolidayPackage
} from "application/features/holidayPackage/holidayPackageSlice";
import BasicInfoTab from "./BasicInfoTab";
import ContentTab from "./ContentTab";
import MediaTab from "./MediaTab";
import { useFormNavigationGuard } from "application/hooks/useFormNavigationGuard";
import UnsavedChangesModal from "components/Quasi/UnsavedChangesModal";

const EditHolidayPackagePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("basic-info");

  // RTK Query hooks
  const { data: listing, isLoading, isError } = useGetHolidayPackageQuery(id);
  const [updateHolidayPackage, { isLoading: isUpdating }] = useUpdateHolidayPackageMutation();
  const [duplicateHolidayPackage, { isLoading: isDuplicating }] = useDuplicateHolidayPackageMutation();

  const holidayPackage = listing?.holidayPackage;

  // Redux state
  const editingHolidayPackage = useSelector(selectEditingHolidayPackage);

  // Navigation guard for unsaved changes
  const { setIsDirty, resetDirty, isModalOpen, handleStay, handleDiscard } = useFormNavigationGuard();

  // Initialize editing state when package data loads
  useEffect(() => {
    if (holidayPackage && editingHolidayPackage?.package_uuid !== holidayPackage.package_uuid) {
      dispatch(setEditingHolidayPackage(holidayPackage));
    }
  }, [holidayPackage, editingHolidayPackage, dispatch]);

  // Clear editing state when leaving the page to avoid stale data
  useEffect(() => {
    return () => {
      dispatch(setEditingHolidayPackage(null));
    };
  }, [dispatch]);

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
  };

  const handleSave = async () => {
    try {
      await updateHolidayPackage(editingHolidayPackage).unwrap();
      toast.success("Pacchetto viaggio aggiornato con successo!");
      resetDirty(); // Reset dirty flag after successful save
    } catch (error) {
      toast.error("Errore nell'aggiornamento del pacchetto viaggio");
      console.error("Update error:", error);
    }
  };

  const handleNavigateToListings = () => {
    navigate("/host/my-properties");
  };

  const handleDuplicatePackage = async () => {
    try {
      const result = await duplicateHolidayPackage(editingHolidayPackage?.package_uuid).unwrap();
      toast.success('Pacchetto viaggio duplicato con successo!');
      // Navigate to edit the new duplicated package
      if (result.package_uuid) {
        navigate(`/host/edit-holiday-package/${result.package_uuid}`);
      } else {
        navigate("/host/my-properties");
      }
    } catch (error) {
      toast.error('Errore nella duplicazione del pacchetto viaggio');
      console.error('Duplicate error:', error);
    }
  };

  const tabs = [
    { id: "basic-info", label: "Info di base", component: BasicInfoTab },
    { id: "content", label: "Contenuto", component: ContentTab },
    { id: "media", label: "Media", component: MediaTab },
  ];

  const ActiveTabComponent = tabs.find(tab => tab.id === activeTab)?.component || BasicInfoTab;

  // Pass setIsDirty to the active tab component
  const tabProps = {
    setIsDirty
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (isError || !holidayPackage) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-red-600 mb-4">
            Errore nel caricamento del pacchetto viaggio
          </h2>
          <ButtonPrimary onClick={handleNavigateToListings}>
            Torna ai miei annunci
          </ButtonPrimary>
        </div>
      </div>
    );
  }

  return (
    <div className="nc-EditHolidayPackagePage">
      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
      />
      {/* Header */}
      <div className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <div className="container">
          <div className="py-6">
            <h1 className="text-2xl font-semibold">Modifica Pacchetto Viaggio</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-1">
              {holidayPackage.name}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <CommonLayout
        packageId={id}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        widePage={activeTab === "content"}
      >
        <ActiveTabComponent {...tabProps} />
      </CommonLayout>

      {/* Bottom Action Buttons */}
      <div className="bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              <span>
                {editingHolidayPackage?.name || 'Pacchetto viaggio'} •
                {editingHolidayPackage?.duration_days || 1} giorni
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <ButtonSecondary onClick={handleNavigateToListings}>
                Annulla
              </ButtonSecondary>
              <ButtonSecondary
                onClick={handleDuplicatePackage}
                loading={isDuplicating}
                disabled={isDuplicating}
              >
                Duplica pacchetto
              </ButtonSecondary>
              <ButtonPrimary
                onClick={handleSave}
                disabled={isUpdating}
                loading={isUpdating}
              >
                {isUpdating ? 'Salvataggio...' : 'Salva modifiche'}
              </ButtonPrimary>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default EditHolidayPackagePage;